/**
 * Jump mode's grid / tilemap map format and its decoder.
 *
 * The canonical Jump map is a **beat-aligned tile grid**: a column is a fixed
 * slice of beat-time (`cellBeats`) and a row is a fixed pixel band (`cellPx`),
 * so column `c` covers beats `[c·cellBeats, (c+1)·cellBeats)` and row `r`
 * covers playfield-Y `[r·cellPx, (r+1)·cellPx)`. This keeps beat-time the master
 * simulation axis (exactly how Geometry Dash works — constant scroll, so
 * horizontal distance = speed × time) while giving authors and the renderer a
 * literal tilemap.
 *
 * The grid is stored as packed row strings (one string per row, each char a
 * tile kind). It compiles — deterministically, here — into the engine's
 * existing piecewise model: a terrain surface (floor tops, with lethal pits),
 * lethal hazards (ground spikes, floating saws, terrain leading-faces) and
 * overhead kill-bounds (ceiling slabs + down-spikes). Both the engine
 * ({@link import('./engine/jump/state.js')}) and the renderer consume
 * {@link decodeJumpGrid}, so collision and visuals stay in lockstep.
 */

/** Tile kinds. Each is a single char in a packed grid row string. */
export const JUMP_TILE = {
  /** Empty air — the cube flies through it. */
  air: '.',
  /** Solid floor / platform. Must connect to the grid bottom (raised = taller stack). */
  floor: '#',
  /** Solid ceiling slab. Must connect to the grid top. */
  ceiling: '=',
  /** Ground spike (points up) — lethal, scores when cleared. Sits on the floor. */
  spikeUp: '^',
  /** Hanging spike (points down) — lethal-only overhead bound, never scores. */
  spikeDown: 'v',
  /** Floating saw — a lethal region you jump over or duck under; scores when cleared. */
  saw: 'o',
} as const;

export type JumpTile = (typeof JUMP_TILE)[keyof typeof JUMP_TILE];

/** Vertical cell count of every Jump grid. */
export const JUMP_GRID_ROWS = 15;

/**
 * Pixel height of one grid row. Chosen so the jump arc lands on clean cell
 * counts without touching the (replay-affecting) physics constants: a short hop
 * (~75 units) clears ~2 cells and a full jump (~150 units) clears ~4 cells.
 * `JUMP_GRID_ROWS · JUMP_CELL_PX` tiles the 540-tall playfield exactly.
 */
export const JUMP_CELL_PX = 36;

/**
 * Default ground thickness in rows (the flat baseline). The baseline floor's
 * TOP surface therefore sits at `(JUMP_GRID_ROWS − JUMP_GROUND_ROWS) · CELL_PX`.
 */
export const JUMP_GROUND_ROWS = 3;

/**
 * Beat width of one grid column. 1/8 beat (8 columns per beat) keeps cells
 * roughly square at the fixed scroll speed and quantises onsets to eighth-notes
 * — musically natural and a fine enough resolution for clusters, saws, and
 * pit edges. Self-describing per grid (stored on {@link JumpGrid}) so a future
 * editor can vary it, but builtins use this constant.
 */
export const JUMP_CELL_BEATS = 0.125;

/**
 * Y of the flat baseline ground's top surface (a grounded cube rests with its
 * bottom edge here). Derived so default terrain matches the row grid.
 */
export const JUMP_GROUND_Y = (JUMP_GRID_ROWS - JUMP_GROUND_ROWS) * JUMP_CELL_PX;

/**
 * Lethal floor Y of a pit / chasm: the grid bottom. A cube that walks off a
 * ledge into a pit falls and "lands" on this line, which the engine treats as a
 * death rather than a landing.
 */
export const JUMP_PIT_DEATH_Y = JUMP_GRID_ROWS * JUMP_CELL_PX;

/** Hard cap on grid columns (≈ maxTotalBeats / cellBeats), a validation bound. */
export const JUMP_MAX_GRID_COLS = 40000;

/** Top Y of a grid row (its upper edge). */
export function jumpRowTopY(row: number): number {
  return row * JUMP_CELL_PX;
}

/** Bottom Y of a grid row (its lower edge). */
export function jumpRowBottomY(row: number): number {
  return (row + 1) * JUMP_CELL_PX;
}

/** First (left-edge) beat of a column. */
export function jumpColStartBeat(grid: JumpGrid, col: number): number {
  return col * grid.cellBeats;
}

/** Centre beat of a column — a ground spike's judgement beat. */
export function jumpColCentreBeat(grid: JumpGrid, col: number): number {
  return (col + 0.5) * grid.cellBeats;
}

/** Column index covering `beat` (floored). */
export function jumpBeatToCol(grid: JumpGrid, beat: number): number {
  return Math.floor(beat / grid.cellBeats);
}

/** Optional render-only coaching pill anchored to a column (tutorial cues). */
export interface JumpGridHint {
  col: number;
  text: string;
}

/** Max length of a {@link JumpGridHint} label. */
export const JUMP_HINT_MAX_LENGTH = 16;

/**
 * The canonical Jump map: a beat-aligned tile grid. `tiles` has exactly `rows`
 * strings, each exactly `cols` chars (a {@link JumpTile}). Compiles to the
 * engine model via {@link decodeJumpGrid}.
 */
export interface JumpGrid {
  rows: number;
  cols: number;
  cellBeats: number;
  cellPx: number;
  tiles: string[];
  hints?: JumpGridHint[];
}

/** A contiguous run of equal-height terrain (the surface the cube rests on). */
export interface JumpTerrainSpan {
  startBeat: number;
  endBeat: number;
  /** Floor-top Y (cube rest line); for a pit, the lethal {@link JUMP_PIT_DEATH_Y}. */
  topY: number;
  /** Chasm: no floor, "landing" here is fatal. */
  pit: boolean;
  /** Floor cell count (0 for a pit) — drives terrain block rendering. */
  floorRows: number;
}

/** A lethal terrain leading-face where the floor steps UP (a wall to clear). */
export interface JumpBlockFace {
  id: string;
  beat: number;
  topY: number;
}

/** A ground spike (or stacked spike-wall) standing on the floor. */
export interface JumpGridSpike {
  id: string;
  col: number;
  beat: number;
  /** Tip Y (top of the topmost ^ cell) — the lethal upper bound for the cube's bottom. */
  tipY: number;
  /** Base Y (the surface it stands on). */
  baseY: number;
}

/** A floating saw: a lethal region the cube must arc over or duck under. */
export interface JumpGridSaw {
  id: string;
  col: number;
  beat: number;
  topY: number;
  bottomY: number;
}

/** A ceiling slab (=) or hanging stalactite run (v) — an overhead kill-bound. */
export interface JumpGridOverhead {
  id: string;
  startBeat: number;
  endBeat: number;
  /** Lethal underside Y (the cube's top must stay below this). */
  bottomY: number;
  /** Top Y of the overhead mass (for rendering). */
  topY: number;
  kind: 'slab' | 'stalactite';
}

/** The engine/render model compiled from a {@link JumpGrid}. */
export interface DecodedJumpGrid {
  terrain: JumpTerrainSpan[];
  blockFaces: JumpBlockFace[];
  spikes: JumpGridSpike[];
  saws: JumpGridSaw[];
  overheads: JumpGridOverhead[];
}

const EPS = 1e-9;

const decodeCache = new WeakMap<JumpGrid, DecodedJumpGrid>();

/** Char at (row, col), or air if out of range. */
function cellAt(grid: JumpGrid, row: number, col: number): string {
  const line = grid.tiles[row];
  if (line === undefined) {
    return JUMP_TILE.air;
  }
  return line.charAt(col) || JUMP_TILE.air;
}

interface VerticalRun {
  topRow: number;
  bottomRow: number;
}

/** Contiguous vertical runs of `tile` in one column, top-to-bottom. */
function verticalRuns(grid: JumpGrid, col: number, tile: string): VerticalRun[] {
  const runs: VerticalRun[] = [];
  let start = -1;
  for (let row = 0; row < grid.rows; row += 1) {
    const isTile = cellAt(grid, row, col) === tile;
    if (isTile && start < 0) {
      start = row;
    } else if (!isTile && start >= 0) {
      runs.push({ topRow: start, bottomRow: row - 1 });
      start = -1;
    }
  }
  if (start >= 0) {
    runs.push({ topRow: start, bottomRow: grid.rows - 1 });
  }
  return runs;
}

/** Contiguous floor cell count counting up from the grid bottom. */
function floorRowsAt(grid: JumpGrid, col: number): number {
  let count = 0;
  for (let row = grid.rows - 1; row >= 0; row -= 1) {
    if (cellAt(grid, row, col) === JUMP_TILE.floor) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

/** Contiguous ceiling-slab cell count counting down from the grid top. */
function ceilingRowsAt(grid: JumpGrid, col: number): number {
  let count = 0;
  for (let row = 0; row < grid.rows; row += 1) {
    if (cellAt(grid, row, col) === JUMP_TILE.ceiling) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

/**
 * Compile a {@link JumpGrid} into the engine/render model. Deterministic and
 * cached per grid object, so the heavy column scan runs once. Ids are derived
 * from cell coordinates so the chart-builder's reachability filter can trace a
 * failing hazard back to the grid cell that produced it.
 */
export function decodeJumpGrid(grid: JumpGrid): DecodedJumpGrid {
  const cached = decodeCache.get(grid);
  if (cached) {
    return cached;
  }

  const { cols } = grid;
  const cellBeats = grid.cellBeats;

  // Per-column floor top (pit = grid bottom) for terrain + leading faces.
  const colFloorRows: number[] = new Array(cols);
  const colTopY: number[] = new Array(cols);
  const colPit: boolean[] = new Array(cols);
  for (let col = 0; col < cols; col += 1) {
    const fr = floorRowsAt(grid, col);
    colFloorRows[col] = fr;
    colPit[col] = fr === 0;
    colTopY[col] = fr === 0 ? JUMP_PIT_DEATH_Y : jumpRowTopY(grid.rows - fr);
  }

  // Merge equal-height adjacent columns into terrain spans.
  const terrain: JumpTerrainSpan[] = [];
  let runStart = 0;
  for (let col = 1; col <= cols; col += 1) {
    const boundary =
      col === cols ||
      colPit[col] !== colPit[runStart] ||
      Math.abs(colTopY[col] - colTopY[runStart]) > EPS;
    if (boundary) {
      terrain.push({
        startBeat: runStart * cellBeats,
        endBeat: col * cellBeats,
        topY: colTopY[runStart],
        pit: colPit[runStart],
        floorRows: colFloorRows[runStart],
      });
      runStart = col;
    }
  }

  // A floor step UP between adjacent spans is a lethal leading face (wall).
  const blockFaces: JumpBlockFace[] = [];
  for (let i = 1; i < terrain.length; i += 1) {
    const prev = terrain[i - 1];
    const cur = terrain[i];
    if (cur.topY < prev.topY - EPS) {
      blockFaces.push({ id: `b${i}`, beat: cur.startBeat, topY: cur.topY });
    }
  }

  // Spikes (^), saws (o) and overheads (= slab, v stalactite) from cell runs.
  const spikes: JumpGridSpike[] = [];
  const saws: JumpGridSaw[] = [];
  const overheads: JumpGridOverhead[] = [];

  // Slab overheads: merge adjacent = columns of equal depth into spans.
  let slabStart = -1;
  let slabRows = 0;
  const flushSlab = (endCol: number): void => {
    if (slabStart < 0) {
      return;
    }
    overheads.push({
      id: `cs${slabStart}`,
      startBeat: slabStart * cellBeats,
      endBeat: endCol * cellBeats,
      bottomY: jumpRowBottomY(slabRows - 1),
      topY: 0,
      kind: 'slab',
    });
    slabStart = -1;
    slabRows = 0;
  };

  for (let col = 0; col < cols; col += 1) {
    const ceilRows = ceilingRowsAt(grid, col);
    if (ceilRows > 0 && ceilRows === slabRows) {
      // extend current slab run
    } else {
      flushSlab(col);
      if (ceilRows > 0) {
        slabStart = col;
        slabRows = ceilRows;
      }
    }

    for (const run of verticalRuns(grid, col, JUMP_TILE.spikeUp)) {
      spikes.push({
        id: `s${col}_${run.topRow}`,
        col,
        beat: jumpColCentreBeat(grid, col),
        tipY: jumpRowTopY(run.topRow),
        baseY: jumpRowBottomY(run.bottomRow),
      });
    }
    for (const run of verticalRuns(grid, col, JUMP_TILE.saw)) {
      saws.push({
        id: `o${col}_${run.topRow}`,
        col,
        beat: jumpColCentreBeat(grid, col),
        topY: jumpRowTopY(run.topRow),
        bottomY: jumpRowBottomY(run.bottomRow),
      });
    }
    for (const run of verticalRuns(grid, col, JUMP_TILE.spikeDown)) {
      overheads.push({
        id: `cv${col}_${run.topRow}`,
        startBeat: jumpColStartBeat(grid, col),
        endBeat: jumpColStartBeat(grid, col + 1),
        bottomY: jumpRowBottomY(run.bottomRow),
        topY: jumpRowTopY(run.topRow),
        kind: 'stalactite',
      });
    }
  }
  flushSlab(cols);

  const decoded: DecodedJumpGrid = { terrain, blockFaces, spikes, saws, overheads };
  decodeCache.set(grid, decoded);
  return decoded;
}

/** Floor-top Y at the spawn column (col 0). The cube spawns resting here. */
export function jumpSpawnTopY(grid: JumpGrid): number {
  const fr = floorRowsAt(grid, 0);
  return fr === 0 ? JUMP_PIT_DEATH_Y : jumpRowTopY(grid.rows - fr);
}

/**
 * Count of distinct hazards a player must clear — spikes, saws, overhead
 * kill-bounds and lethal terrain leading-faces (step-ups + pit cliffs). Drives
 * the "N obstacles" labels on the home / play pages.
 */
export function jumpObstacleCount(grid: JumpGrid): number {
  const d = decodeJumpGrid(grid);
  return d.spikes.length + d.saws.length + d.overheads.length + d.blockFaces.length;
}

// ---------------------------------------------------------------------------
// Authoring helpers (used by the auto-generator + the hand-authored tutorial).
// A grid is built as a mutable char matrix, then packed into row strings.
// ---------------------------------------------------------------------------

/** A mutable char matrix `[row][col]` — the editable working form of a grid. */
export type JumpCharGrid = string[][];

/** Create an all-air char matrix sized `rows × cols`. */
export function createJumpCharGrid(rows: number, cols: number): JumpCharGrid {
  const grid: JumpCharGrid = [];
  for (let r = 0; r < rows; r += 1) {
    grid.push(new Array<string>(cols).fill(JUMP_TILE.air));
  }
  return grid;
}

/** Set a cell, ignoring out-of-range coordinates. */
export function setJumpCell(grid: JumpCharGrid, row: number, col: number, tile: string): void {
  if (row < 0 || row >= grid.length) {
    return;
  }
  const line = grid[row];
  if (col < 0 || col >= line.length) {
    return;
  }
  line[col] = tile;
}

/** Get a cell, returning air for out-of-range coordinates. */
export function getJumpCell(grid: JumpCharGrid, row: number, col: number): string {
  if (row < 0 || row >= grid.length) {
    return JUMP_TILE.air;
  }
  return grid[row][col] ?? JUMP_TILE.air;
}

/**
 * Paint floor across `[startCol, endCol)` to `floorRows` tall (from the bottom).
 * `floorRows` 0 leaves a pit; clears any taller floor first so the height is set
 * absolutely, not additively.
 */
export function paintFloor(
  grid: JumpCharGrid,
  startCol: number,
  endCol: number,
  floorRows: number,
): void {
  const rows = grid.length;
  for (let col = startCol; col < endCol; col += 1) {
    for (let row = 0; row < rows; row += 1) {
      const fromBottom = rows - row;
      if (getJumpCell(grid, row, col) === JUMP_TILE.floor && fromBottom > floorRows) {
        setJumpCell(grid, row, col, JUMP_TILE.air);
      }
    }
    for (let r = 0; r < floorRows; r += 1) {
      setJumpCell(grid, rows - 1 - r, col, JUMP_TILE.floor);
    }
  }
}

/** Paint a ceiling slab across `[startCol, endCol)`, `ceilRows` tall from the top. */
export function paintCeiling(
  grid: JumpCharGrid,
  startCol: number,
  endCol: number,
  ceilRows: number,
): void {
  for (let col = startCol; col < endCol; col += 1) {
    for (let r = 0; r < ceilRows; r += 1) {
      setJumpCell(grid, r, col, JUMP_TILE.ceiling);
    }
  }
}

/** Pack a char matrix into the {@link JumpGrid} row-string form. */
export function packJumpGrid(
  charGrid: JumpCharGrid,
  cellBeats: number,
  cellPx: number,
  hints?: JumpGridHint[],
): JumpGrid {
  const rows = charGrid.length;
  const cols = rows > 0 ? charGrid[0].length : 0;
  const tiles = charGrid.map((line) => line.join(''));
  const grid: JumpGrid = { rows, cols, cellBeats, cellPx, tiles };
  if (hints && hints.length > 0) {
    grid.hints = hints;
  }
  return grid;
}

/** Floor-row count (from the bottom) whose top edge sits at row index `topRow`. */
export function jumpFloorRowsForTopRow(topRow: number): number {
  return JUMP_GRID_ROWS - topRow;
}
